import React from "react";
import { Stack } from "@mui/material";
import ClampedText from "./ClampedText";
import { DIFF_OPS, EMPTY_VALUE, REVIEW_COPY } from "./reviewChanges.consts";
import {
  ChangedCell,
  DeletedText,
  DiffLabel,
  FieldValueBox,
  IdText,
  InsertedText,
  NowLabel,
  PlaceholderText
} from "./ReviewChangesDialog.styles";

const segmentsFor = (segments, hidden) => segments
  .filter(({ op }) => op !== hidden)
  .map(({ text, op }, index) => {
    if (op === DIFF_OPS.DELETED) return <DeletedText key={index}>{text}</DeletedText>;
    if (op === DIFF_OPS.INSERTED) return <InsertedText key={index}>{text}</InsertedText>;
    return <span key={index}>{text}</span>;
  });

// One side of a field change: WAS (shaded) or NOW (outlined); long text shows its word diff.
export const ValueBox = ({ current = false, label, text, segments }) => {
  const Label = current ? NowLabel : DiffLabel;
  return (
    <FieldValueBox current={current}>
      <Label>{label}</Label>
      <ClampedText text={text ?? EMPTY_VALUE} muted={!current}>
        {segments ? segmentsFor(segments, current ? DIFF_OPS.DELETED : DIFF_OPS.INSERTED) : null}
      </ClampedText>
    </FieldValueBox>
  );
};

// WAS / NOW of an edited cell, on a shaded background.
// `beforeNode` / `afterNode` replace the text when the value renders as a component (a reference label).
const WasNowCell = ({ before, after, beforeNode, afterNode }) => (
  <ChangedCell data-testid="was-now-cell">
    <Stack direction="row" alignItems="baseline">
      <DiffLabel>{REVIEW_COPY.WAS}</DiffLabel>
      {beforeNode ?? <ClampedText text={before} muted />}
    </Stack>
    <Stack direction="row" alignItems="baseline">
      <NowLabel>{REVIEW_COPY.NOW}</NowLabel>
      {afterNode ?? <ClampedText text={after} />}
    </Stack>
  </ChangedCell>
);

// `renderStored(value, isBefore)` (when given) renders a stored value — a reference column shows its target's
// label, looked up in the live version for what was there before (the WAS side, a removed row).
export const RecordValueCell = ({ cell, renderStored, stored, previousStored, storedIsBefore = false }) => {
  if (renderStored) {
    return cell?.changed
      ? <WasNowCell beforeNode={renderStored(previousStored, true)} afterNode={renderStored(stored, false)} />
      : renderStored(stored, storedIsBefore);
  }
  if (cell?.changed) return <WasNowCell before={cell.previous} after={cell.value} />;
  if (cell?.placeholder) {
    return (
      <Stack gap={0.25}>
        <PlaceholderText>{cell.value}</PlaceholderText>
        <IdText>{cell.detail}</IdText>
      </Stack>
    );
  }
  return <ClampedText text={cell?.value} />;
};
